import { orderFormOf, type OrderFormValues } from '@/components/santa-video/order-form-body'
import type { VideoLocale } from '@/server/domain/santa-video/locale'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

/** Closed categories only: never retain the referrer URL or its query. */
export function videoReferrerType(referrer: string, origin: string): 'direct' | 'internal' | 'organic' | 'referral' {
  if (!referrer) return 'direct'
  try {
    const url = new URL(referrer)
    if (url.origin === origin) return 'internal'
    const host = url.hostname
    if (/^(www\.)?(google\.(?:[a-z]{2,3}|(?:com|co)\.[a-z]{2})|bing\.com|duckduckgo\.com|search\.yahoo\.com)$/.test(host)) return 'organic'
    return 'referral'
  } catch {
    return 'referral'
  }
}

/**
 * The optional keys of each story's request `form`. Counted on what
 * `orderFormOf` actually sends, so blank fields and fields a story does not
 * have are already gone: the book sends no gifts, its interests are
 * mandatory, and only it sends `challenge`.
 */
const OPTIONAL_KEYS = {
  letter: ['age', 'location', 'parentMessage', 'interests', 'requestedGifts', 'authorizedGifts'],
  book: ['age', 'location', 'parentMessage', 'challenge'],
} as const satisfies Record<VideoStoryId, readonly string[]>

const sent = (value: unknown) => value !== undefined && !(Array.isArray(value) && value.length === 0)

/** Count the story's optional field groups, not list entries or characters of personal text. */
export function optionalVideoFieldsUsed(values: OrderFormValues, story: VideoStoryId): number {
  const form = orderFormOf(values, story)
  return OPTIONAL_KEYS[story].filter((key) => sent(form[key])).length
}

/** What every video funnel event carries: one owner for the rule. */
export type VideoFunnelEnvelope = { product: 'video'; story: VideoStoryId; locale: VideoLocale }

export function videoFunnelEnvelope(story: VideoStoryId, locale: VideoLocale): VideoFunnelEnvelope {
  return { product: 'video', story, locale }
}
